import cn from 'classnames';
import { type FC, useMemo } from 'react';

import { TaskScheduleFrequencyCell } from '@/components/Studies/Settings/Settings.utils';
import { getTargetNameForTasks } from '@/components/Tasks/Show/Tasks.utils';
import Link from '@/components/UI/Link';
import Table from '@/components/UI/Table';
import type { TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';
import { _isNotBlank, _notNil } from '@/littledash';
import type { Study } from '@/model/Study.model';
import type { TaskTarget, TaskType } from '@/model/Task.model';
import { DateRenderFormat, DateUtils } from '@/utils/Date.utils';
import { useModalAction } from '@/utils/modal';

import type { SuccessfulOverviewSection } from '../../OverviewAccordion.model';
import { useOverviewAccordion } from '../../OverviewAccordionContext';
import { canUpdateOverviewTask } from '../../overviewSectionAccess';
import OverviewGroupChips from '../OverviewGroupChips';
import overviewGroupChipsStyles from '../OverviewGroupChips.module.scss';
import overviewTableStyles from '../OverviewSectionTable.module.scss';
import { useUpdateTaskModal } from './useUpdateTaskModal';

interface TasksSectionContentProps {
  section: SuccessfulOverviewSection<'tasks'>;
  study?: Study;
  studyId?: string;
  onTaskSaved?: () => void;
}

type OverviewTask = SuccessfulOverviewSection<'tasks'>['rows'][number];
type OverviewTaskSchedule = NonNullable<OverviewTask['schedule']>;

const emptyValue = '-';

const taskTypeLabels: Record<TaskType, string> = {
  measurement: 'Measurement',
  sample: 'Sample',
  observation: 'Observation',
  dosing: 'Dosing',
  other: 'Other',
};

const formatOptionalText = (value: string | undefined): string => (_isNotBlank(value) ? value : emptyValue);

const formatScheduleDate = (schedule: OverviewTaskSchedule | undefined, boundary: 'start' | 'end'): string => {
  if (!_notNil(schedule) || schedule.type === 'conditional') {
    return emptyValue;
  }

  return DateUtils.renderDateTime(schedule.duration[boundary], {
    format: DateRenderFormat.Date,
    timezone: schedule.timezone ?? undefined,
  });
};

const formatTarget = (target: TaskTarget | undefined): string =>
  _notNil(target) ? getTargetNameForTasks(target) : emptyValue;

/**
 * Renders compact study task specs in the Overview accordion.
 */
const TasksSectionContent: FC<TasksSectionContentProps> = ({ section, study, studyId, onTaskSaved }) => {
  const { groupsByApiId } = useOverviewAccordion();
  const groups = useMemo(() => Array.from(groupsByApiId.values()), [groupsByApiId]);
  const { openModal } = useModalAction();
  const { loading, openUpdateTask } = useUpdateTaskModal({ study, onSaved: onTaskSaved });
  const canUpdateTask = canUpdateOverviewTask(study);
  const tasks = section.rows;
  const columns: Array<TanStackColumnDef<OverviewTask>> = [
    {
      id: 'title',
      header: 'Title',
      accessorKey: 'title',
      cell: ({
        row: {
          original: { api_id: taskSpecApiId, title },
        },
      }) =>
        canUpdateTask ? (
          <Link
            className="link blue"
            data-testid={`edit-task-${taskSpecApiId}`}
            onClick={(event) => {
              event.preventDefault();
              void openUpdateTask(taskSpecApiId);
            }}
          >
            {formatOptionalText(title)}
          </Link>
        ) : (
          formatOptionalText(title)
        ),
    },
    {
      id: 'description',
      header: 'Description',
      accessorKey: 'description',
      cell: ({
        row: {
          original: { description },
        },
      }) => formatOptionalText(description),
    },
    {
      id: 'type',
      header: 'Type',
      accessorKey: 'type',
      cell: ({
        row: {
          original: { type },
        },
      }) => taskTypeLabels[type],
    },
    {
      id: 'target',
      header: 'Target',
      accessorKey: 'target',
      cell: ({
        row: {
          original: { target, title },
        },
      }) =>
        target?.type === 'group' ? (
          <span
            className={cn('pointer', overviewGroupChipsStyles.fill)}
            data-testid="overview-task-target"
            onClick={() =>
              openModal('TASK_TARGET_DETAILS', {
                target,
                groups,
                studyId: studyId ?? null,
                taskTitle: title,
              })
            }
          >
            <OverviewGroupChips groupApiIds={target.groups} />
          </span>
        ) : (
          <span data-testid="overview-task-target">{formatTarget(target)}</span>
        ),
    },
    {
      id: 'frequency',
      header: 'Frequency',
      accessorKey: 'schedule',
      cell: ({
        row: {
          original: { schedule },
        },
      }) => (_notNil(schedule) ? <TaskScheduleFrequencyCell schedule={schedule} /> : emptyValue),
    },
    {
      id: 'start',
      header: 'Start',
      accessorKey: 'schedule',
      cell: ({
        row: {
          original: { schedule },
        },
      }) => formatScheduleDate(schedule, 'start'),
    },
    {
      id: 'end',
      header: 'End',
      accessorKey: 'schedule',
      cell: ({
        row: {
          original: { schedule },
        },
      }) => formatScheduleDate(schedule, 'end'),
    },
  ];

  return (
    <div
      className={cn({ ui__disabled: loading })}
      data-test-component="TasksSectionContent"
      data-test-element="container"
    >
      <Table data={tasks} columns={columns} maxHeightClassName={overviewTableStyles.maxHeight} />
    </div>
  );
};

export default TasksSectionContent;
